import React, { useState, useEffect, useRef } from "react";
import { siteConfig } from "../../config/siteConfig";
import Icon from "../ui/Icon";
import SealChip from "../ui/SealChip";

const CTASection = ({ onCtaClick }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { uiCopy, contact, sealLabels } = siteConfig;
  const cta = uiCopy.cta;
  const reassuranceLabel = sealLabels?.verified || "";
  const modalTitle = cta.contactModalTitle || "Contact";
  const modalSubtitle =
    cta.contactModalSubtitle || "Have a question? Write to us directly:";
  const modalCloseLabel = cta.contactModalClose || "Close";
  const modalCloseAriaLabel = cta.contactModalCloseAriaLabel || modalCloseLabel;
  const modalRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const previouslyFocused = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsModalOpen(false);
        return;
      }
      if (event.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const opener = openerRef.current;
    const focusTarget = modalRef.current?.querySelector(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    focusTarget?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      } else if (opener) {
        opener.focus();
      }
    };
  }, [isModalOpen]);

  return (
    <section className="py-28 md:py-36 relative overflow-hidden">
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grain relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950 px-8 py-14 md:px-16 md:py-20 text-center ring-1 ring-white/10 elev-3">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.18] pointer-events-none"
          >
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-teal-500 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-cyan-500 blur-3xl" />
          </div>

          <div className="relative">
            {reassuranceLabel ? (
              <div className="flex justify-center mb-8">
                <SealChip label={reassuranceLabel} icon="seal" tone="teal" />
              </div>
            ) : null}

            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-balance text-white mb-6">
              {cta.finalSectionTitle}
            </h2>
            <p className="text-lg md:text-xl text-pretty text-white/80 mb-10 max-w-2xl mx-auto leading-snug">
              {cta.finalSectionSubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onCtaClick}
                className="btn-primary glow-cta group text-lg px-8 py-4"
              >
                <span>{cta.finalSectionPrimary}</span>
                <svg
                  className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <button
                ref={openerRef}
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg font-semibold text-white ring-1 ring-white/30 transition-all duration-200 ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[.98]"
              >
                {cta.finalSectionSecondary}
              </button>
            </div>
            <p className="text-sm text-white/60 mt-8">{cta.finalSectionFooter}</p>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setIsModalOpen(false)} />
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cta-contact-modal-title"
            className="relative bg-white rounded-2xl p-8 md:p-12 max-w-md w-full elev-3 animate-in fade-in zoom-in duration-300"
          >
            <button onClick={() => setIsModalOpen(false)} aria-label={modalCloseAriaLabel} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-50 text-teal-700 rounded-2xl flex items-center justify-center mx-auto mb-6 ring-1 ring-inset ring-teal-100">
                <Icon name="document-check" className="w-8 h-8" />
              </div>
              <h3 id="cta-contact-modal-title" className="text-2xl font-bold tracking-tight text-slate-900 mb-2">{modalTitle}</h3>
              <p className="text-slate-500 mb-8">{modalSubtitle}</p>
              <a href={`mailto:${contact.salesEmail}`} className="block w-full py-4 bg-slate-100 rounded-xl text-xl font-bold text-teal-700 hover:bg-teal-50 transition-colors border-2 border-dashed border-teal-200 mb-6">
                {contact.salesEmail}
              </a>
              <button onClick={() => setIsModalOpen(false)} className="text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors">
                {modalCloseLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CTASection;
